<template>
  <div class="dx-action-menu">
    <button ref="menuBtn" class="dx-action-menu__btn" type="button" :disabled="disabled">
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="3.5" cy="9" r="1.5" fill="currentColor" />
        <circle cx="9" cy="9" r="1.5" fill="currentColor" />
        <circle cx="14.5" cy="9" r="1.5" fill="currentColor" />
      </svg>
    </button>
    <DxContextMenu
      v-if="menuTarget"
      ref="contextMenu"
      :data-source="menuItems"
      :target="menuTarget"
      :show-event="'dxclick'"
      :hide-on-outside-click="true"
      :width="menuWidth"
      :css-class="menuClass"
      @item-click="onItemClick"
    >
      <template #item="{ data }">
        <div class="dx-action-menu__item" :class="data.class">
          <img v-if="data.icon" :src="data.icon" width="13" height="13" class="dx-action-menu__icon" :alt="data.label || data.text" />
          <i v-else-if="data.iconClass" :class="['dx-action-menu__icon', data.iconClass]" aria-hidden="true"></i>
          <span>{{ data.label || data.text }}</span>
        </div>
      </template>
    </DxContextMenu>
  </div>
</template>

<script>
import { DxContextMenu } from "devextreme-vue/context-menu";

export default {
  name: "DxActionMenu",
  components: {
    DxContextMenu,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    allowDeleting: {
      type: Function,
      default: null,
    },
    gridRef: {
      type: Object,
      default: null,
    },
    rowKey: {
      type: [String, Number],
      default: null,
    },
    editAction: {
      type: Function,
      default: null,
    },
    deleteAction: {
      type: Function,
      default: null,
    },
    editLabel: {
      type: String,
      default: "",
    },
    deleteLabel: {
      type: String,
      default: "",
    },
    editIcon: {
      type: String,
      default: "",
    },
    deleteIcon: {
      type: String,
      default: "",
    },
    row: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    menuWidth: {
      type: Number,
      default: 140,
    },
    menuClass: {
      type: String,
      default: "smagria-dx-action-menu__popup",
    },
    mergeWithDefaultItems: {
      type: Boolean,
      default: true,
    },
    emptyText: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      menuTarget: null,
    };
  },
  computed: {
    resolvedEmptyText() {
      return this.emptyText || this.$t("common.no_available_function");
    },
    menuItems() {
      const defaultItems = typeof this.getDefaultItems === "function" ? this.getDefaultItems() : [];
      const safeDefaultItems = Array.isArray(defaultItems) ? defaultItems : [];
      
      // Assign default position to default items (0 for edit, 1 for delete)
      safeDefaultItems.forEach((item, idx) => {
        if (item.position === undefined) {
          item.position = idx;
        }
      });

      let allItems = [];
      if (this.items && this.items.length) {
        allItems = this.mergeWithDefaultItems ? [...this.items, ...safeDefaultItems] : this.items;
      } else if (safeDefaultItems.length) {
        allItems = safeDefaultItems;
      } else {
        return [
          {
            key: "__empty",
            label: this.resolvedEmptyText,
            text: this.resolvedEmptyText,
            class: "is-empty",
            isEmpty: true,
          },
        ];
      }

      // Sort items by position if specified
      return allItems.sort((a, b) => {
        const posA = a.position !== undefined ? a.position : 999;
        const posB = b.position !== undefined ? b.position : 999;
        return posA - posB;
      });
    },
  },
  watch: {
    disabled(disabled) {
      if (!disabled) return;

      this.$nextTick(() => {
        const menu = this.$refs.contextMenu && this.$refs.contextMenu.instance;
        if (menu) menu.hide();
      });
    },
  },
  mounted() {
    this.menuTarget = this.$refs.menuBtn;
  },
  methods: {
    getDefaultItems() {
      const allowDelete = this.allowDeleting ? (this.row ? this.allowDeleting(this.row) : false) : this.canDelete;
      const items = [];

      if (this.canEdit && (this.editAction || this.canEditRow)) {
        items.push({
          key: "edit",
          label: this.editLabel || this.$t("common.edit"),
          text: this.editLabel || this.$t("common.edit"),
          icon: this.editIcon || require("@/assets/images/icons/edit.svg"),
          class: "",
          action: this.editAction || this.onEditDefault,
        });
      }

      if (allowDelete && (this.deleteAction || this.canDeleteRow)) {
        items.push({
          key: "delete",
          label: this.deleteLabel || this.$t("common.delete"),
          text: this.deleteLabel || this.$t("common.delete"),
          icon: this.deleteIcon || require("@/assets/images/desktop/icons/controls/delete.svg"),
          class: "delete",
          action: this.deleteAction || this.onDeleteDefault,
        });
      }

      return items;
    },
    getGridInstance() {
      return this.gridRef && this.gridRef.instance ? this.gridRef.instance : null;
    },
    getRowIndex(row) {
      const instance = this.getGridInstance();
      if (!instance || !row || this.rowKey === null) return -1;
      return instance.getRowIndexByKey(row[this.rowKey]);
    },
    canEditRow(row) {
      return !!(this.gridRef && row && this.rowKey !== null);
    },
    canDeleteRow(row) {
      return !!(this.gridRef && row && this.rowKey !== null);
    },
    onEditDefault(row) {
      const instance = this.getGridInstance();
      const rowIndex = this.getRowIndex(row);
      if (!instance || rowIndex < 0) return;
      instance.editRow(rowIndex);
    },
    onDeleteDefault(row) {
      const instance = this.getGridInstance();
      const rowIndex = this.getRowIndex(row);
      if (!instance || rowIndex < 0) return;
      instance.deleteRow(rowIndex);
    },
    onItemClick(e) {
      const item = e && e.itemData ? e.itemData : null;
      if (item && item.isEmpty) return;
      if (item && typeof item.action === "function") {
        item.action(this.row);
      }
      this.$emit("select", { item, row: this.row });
    },
  },
};
</script>

<style scoped>
.dx-action-menu {
  display: inline-flex;
  align-items: center;
}
.dx-action-menu__btn {
  background: #fff;
  border: 1px solid #898989;
  border-radius: 999px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #898989;
}
.dx-action-menu__btn:hover {
  border-color: #1a8f3d;
  color: #1a8f3d;
}
.dx-action-menu__item {
  padding: 8px 8px 8px 8px !important;
  cursor: pointer;
  color: #898989;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
  border-radius: 6px;
  letter-spacing: 0.02em;
}
.dx-action-menu__item:hover {
  background: #f5f5f5;
  color: #1a8f3d;
}
.dx-action-menu__item.delete:hover {
  color: #d32f2f;
}
.dx-action-menu__item.is-empty {
  cursor: default;
  color: #b0b0b0;
}
.dx-action-menu__item.is-empty:hover {
  background: transparent;
  color: #b0b0b0;
}
.dx-action-menu__icon {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  filter: brightness(0) saturate(100%) invert(60%) sepia(3%) saturate(9%) hue-rotate(314deg) brightness(93%) contrast(88%);
}
.dx-action-menu__item:hover .dx-action-menu__icon {
  filter: brightness(0) saturate(100%) invert(41%) sepia(73%) saturate(480%) hue-rotate(90deg) brightness(95%) contrast(92%);
}
.dx-action-menu__item.delete:hover .dx-action-menu__icon {
  filter: brightness(0) saturate(100%) invert(19%) sepia(92%) saturate(3000%) hue-rotate(0deg) brightness(105%) contrast(100%);
}
</style>

<style>
.smagria-dx-action-menu__popup .dx-submenu {
  padding: 8px !important;
  border-radius: 6px !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}
</style>
